/**
 * The Bin — a 7-day holding area for deleted tasks.
 *
 * Born from a real alpha incident (28 Jul 2026): an accidental tap on the
 * delete icon destroyed a task with no recovery path. Deleting now MOVES the
 * task here instead; it can be restored with everything intact (plan, tags,
 * notes, dates) for BIN_RETENTION_MS, after which it is purged automatically.
 *
 * The bin is SYNCED, and its entries double as deletion TOMBSTONES: a task
 * present in the merged bin with a `deletedAt` newer than the task's own
 * edit stamp is a deletion the other device hasn't seen yet — the merge
 * removes it from the board instead of resurrecting it. This retires the
 * old "deletion-resurrection: annoying beats fatal" trade-off for every
 * deletion that goes through the bin. Restoring bumps the task's `editedAt`
 * past `deletedAt`, so a restore always beats a stale tombstone.
 */

import type { Task } from '../types';

export interface BinEntry {
  task: Task;
  /** Epoch ms of the deletion — the tombstone clock. */
  deletedAt: number;
  /**
   * Epoch ms when the user emptied this entry early ("Delete forever",
   * Anjuli Johnson, 2 Sept 2026 — she was rebuilding her board from scratch
   * and wanted finished deletions out of sight before the 7 days elapsed).
   *
   * ⚠ A purged entry is HIDDEN, NOT REMOVED, and this is the whole point.
   * The entry IS the tombstone (see the header): drop it from the array and
   * any device that has not yet synced still holds the task alive, so the
   * next merge unions it straight back onto the board. The deletion would
   * undo itself — the exact failure the Bin was built to retire.
   *
   * So a purge keeps `deletedAt` (the tombstone keeps working, and still
   * expires on the original 7-day clock) and REDACTS the payload, so
   * "delete forever" is honest about the text being gone rather than merely
   * hidden. Absent on normal entries; optional so older builds ignore it
   * and `push()`'s carry preserves it.
   */
  purgedAt?: number;
}

/** How long deleted tasks are kept before automatic purge. */
export const BIN_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const stamp = (t: Task) => t.editedAt ?? t.createdAt;

/** Drop entries older than the retention window. */
export function purgeBin(bin: BinEntry[], now: number): BinEntry[] {
  return bin.filter((e) => now - e.deletedAt < BIN_RETENTION_MS);
}

/**
 * Add a task to the bin (newest first). Re-deleting a task that already has
 * an entry replaces the old entry — one tombstone per task id. Purges as it
 * goes so the bin never grows stale entries.
 */
export function toBin(bin: BinEntry[], task: Task, now: number): BinEntry[] {
  return [
    { task, deletedAt: now },
    ...purgeBin(bin, now).filter((e) => e.task.id !== task.id),
  ];
}

/** What a purged entry's text becomes — see the note in `redactTask`. */
export const PURGED_LABEL = '(permanently deleted)';

/**
 * Strip a purged entry's payload down to what the tombstone machinery
 * actually reads. `applyTombstones` and `mergeBins` only ever touch
 * `e.task.id`; nothing reads the rest of a purged entry on THIS build,
 * because purged entries are never listed and never restorable here.
 * Keeping `id`, `zone` and `createdAt` costs nothing and keeps the value a
 * valid `Task`.
 */
function redactTask(t: Task): Task {
  return {
    id: t.id,
    // NOT empty string. Older builds (Android build 16, a stale PWA) do not
    // know `purgedAt`, so they will list this entry in their Bin and render
    // `e.task.text` verbatim — an empty row with a Restore button reads as a
    // corrupted app. A label explains itself, and restoring it on an old
    // build yields a task that says what it is instead of a blank mystery.
    // Remove this allowance only once build 16 is off the estate.
    text: PURGED_LABEL,
    zone: t.zone,
    urgency: 0,
    importance: 0,
    steps: [],
    stepsGenerated: false,
    done: false,
    rolloverCount: 0,
    createdAt: t.createdAt,
  };
}

/** Entries the user should see — purged ones are gone as far as the UI knows. */
export function visibleBin(bin: BinEntry[]): BinEntry[] {
  return bin.filter((e) => !e.purgedAt);
}

/**
 * "Delete forever" on ONE entry: redact the payload, keep the tombstone.
 * A no-op for an id that is not in the bin, and idempotent on one already
 * purged (the first `purgedAt` stands).
 */
export function purgeEntry(bin: BinEntry[], taskId: string, now: number): BinEntry[] {
  return bin.map((e) =>
    e.task.id === taskId && !e.purgedAt
      ? { ...e, task: redactTask(e.task), purgedAt: now }
      : e,
  );
}

/** "Empty bin": the same treatment applied to every visible entry. */
export function purgeAllEntries(bin: BinEntry[], now: number): BinEntry[] {
  return bin.map((e) =>
    e.purgedAt ? e : { ...e, task: redactTask(e.task), purgedAt: now },
  );
}

/**
 * Union two bins by task id — the newer deletion wins. Purged + capped.
 *
 * Tie-break on equal `deletedAt`: the PURGED entry wins. Without this the
 * winner would depend on array order, so a device that purged an entry could
 * have the un-redacted copy pushed back at it by a device that had not, and
 * the item would reappear in the list the user just cleared. "Purge wins" is
 * the only direction that is stable: it can never un-hide something.
 */
export function mergeBins(a: BinEntry[], b: BinEntry[], now: number): BinEntry[] {
  const byId = new Map<string, BinEntry>();
  for (const e of [...a, ...b]) {
    const prev = byId.get(e.task.id);
    if (!prev) {
      byId.set(e.task.id, e);
      continue;
    }
    if (e.deletedAt > prev.deletedAt) byId.set(e.task.id, e);
    else if (e.deletedAt === prev.deletedAt && e.purgedAt && !prev.purgedAt) {
      byId.set(e.task.id, e);
    }
  }
  return purgeBin([...byId.values()], now)
    .sort((x, y) => y.deletedAt - x.deletedAt)
    .slice(0, 200);
}

/**
 * Apply deletion tombstones to a merged task board.
 *
 * For each bin entry whose task id is on the board:
 * - deletion newer than the task's last edit → the delete is the latest
 *   user intent: remove the task from the board (tombstone wins).
 * - task edited (or restored) AFTER the deletion → the task is the latest
 *   intent: keep it on the board and drop the bin entry, so it doesn't
 *   linger as both live and binned.
 */
export function applyTombstones(
  tasks: Record<string, Task>,
  bin: BinEntry[],
): { tasks: Record<string, Task>; bin: BinEntry[] } {
  const outTasks = { ...tasks };
  const outBin: BinEntry[] = [];
  for (const e of bin) {
    const live = outTasks[e.task.id];
    if (live && stamp(live) > e.deletedAt) continue; // restored/edited after delete
    if (live) delete outTasks[e.task.id]; // tombstone wins
    outBin.push(e);
  }
  return { tasks: outTasks, bin: outBin };
}

/** Whole days remaining before an entry is purged (minimum 0). */
export function binDaysLeft(entry: BinEntry, now: number): number {
  const remaining = entry.deletedAt + BIN_RETENTION_MS - now;
  return Math.max(0, Math.ceil(remaining / (24 * 60 * 60 * 1000)));
}
